import type {
  MockCache,
  OpEntityTable,
  TaskId,
  ChecklistItemId,
  CommentId,
  TaskLinkId,
  TimeEntryId,
  MessageId,
  ReactionId,
  InboxItemId,
  Task,
  ActivityEvent,
  ChecklistItem,
  Comment,
  TaskLink,
  TimeEntry,
  Message,
  Reaction,
  InboxItem,
} from '../types';

export function idxPush<K, V>(map: Map<K, V[]>, key: K, id: V): void {
  let list = map.get(key);
  if (list === undefined) {
    list = [];
    map.set(key, list);
  }
  if (!list.includes(id)) {
    list.push(id);
  }
}

export function idxPull<K, V>(map: Map<K, V[]>, key: K, id: V): void {
  const list = map.get(key);
  if (list === undefined) {
    return;
  }
  const at = list.indexOf(id);
  if (at >= 0) {
    list.splice(at, 1);
  }
}

function freshIndex(): MockCache['index'] {
  return {
    taskByKey: new Map(),
    tasksByProject: new Map(),
    tasksByStatus: new Map(),
    tasksByAssignee: new Map(),
    subtasksByParent: new Map(),
    checklistByTask: new Map(),
    commentsByTask: new Map(),
    linksByTask: new Map(),
    timeByTask: new Map(),
    messagesByChannel: new Map(),
    repliesByThread: new Map(),
    reactionsByOwner: new Map(),
    activityByTask: new Map(),
    inboxByUser: new Map(),
  };
}

export function addToIndex(cache: MockCache, table: OpEntityTable, id: string): void {
  const index = cache.index;
  switch (table) {
    case 'task': {
      const task = cache.tasks.get(id as TaskId);
      if (task === undefined) {
        return;
      }
      index.taskByKey.set(task.key, task.id);
      if (task.deletedAt !== null) {
        return;
      }
      idxPush(index.tasksByProject, task.projectId, task.id);
      idxPush(index.tasksByStatus, task.status, task.id);
      if (task.assigneeId !== null) {
        idxPush(index.tasksByAssignee, task.assigneeId, task.id);
      }
      if (task.parentId !== null) {
        idxPush(index.subtasksByParent, task.parentId, task.id);
      }
      return;
    }
    case 'checklistItem': {
      const item = cache.checklist.get(id as ChecklistItemId);
      if (item === undefined || item.deletedAt !== null) {
        return;
      }
      idxPush(index.checklistByTask, item.taskId, item.id);
      return;
    }
    case 'comment': {
      const comment = cache.comments.get(id as CommentId);
      if (comment === undefined || comment.deletedAt !== null) {
        return;
      }
      idxPush(index.commentsByTask, comment.taskId, comment.id);
      return;
    }
    case 'taskLink': {
      const link = cache.links.get(id as TaskLinkId);
      if (link === undefined) {
        return;
      }
      idxPush(index.linksByTask, link.fromTaskId, link.id);
      if (link.toTaskId !== link.fromTaskId) {
        idxPush(index.linksByTask, link.toTaskId, link.id);
      }
      return;
    }
    case 'timeEntry': {
      const entry = cache.timeEntries.get(id as TimeEntryId);
      if (entry === undefined) {
        return;
      }
      idxPush(index.timeByTask, entry.taskId, entry.id);
      return;
    }
    case 'message': {
      const message = cache.messages.get(id as MessageId);
      if (message === undefined) {
        return;
      }
      if (message.threadRootId === null) {
        idxPush(index.messagesByChannel, message.channelId, message.id);
      } else {
        idxPush(index.repliesByThread, message.threadRootId, message.id);
      }
      return;
    }
    case 'reaction': {
      const reaction = cache.reactions.get(id as ReactionId);
      if (reaction === undefined) {
        return;
      }
      idxPush(index.reactionsByOwner, `${reaction.ownerKind}:${String(reaction.ownerId)}`, reaction.id);
      return;
    }
    case 'inboxItem': {
      const item = cache.inbox.get(id as InboxItemId);
      if (item === undefined) {
        return;
      }
      idxPush(index.inboxByUser, item.userId, item.id);
      return;
    }
    default: {
      return;
    }
  }
}

export function removeFromIndex(cache: MockCache, table: OpEntityTable, id: string): void {
  const index = cache.index;
  switch (table) {
    case 'task': {
      const task = cache.tasks.get(id as TaskId);
      index.taskByKey.delete((task?.key ?? id) as Task['key']);
      if (task !== undefined) {
        idxPull(index.tasksByProject, task.projectId, task.id);
        idxPull(index.tasksByStatus, task.status, task.id);
        if (task.assigneeId !== null) {
          idxPull(index.tasksByAssignee, task.assigneeId, task.id);
        }
        if (task.parentId !== null) {
          idxPull(index.subtasksByParent, task.parentId, task.id);
        }
      }
      return;
    }
    case 'checklistItem': {
      const item = cache.checklist.get(id as ChecklistItemId);
      if (item !== undefined) {
        idxPull(index.checklistByTask, item.taskId, item.id);
      }
      return;
    }
    case 'comment': {
      const comment = cache.comments.get(id as CommentId);
      if (comment !== undefined) {
        idxPull(index.commentsByTask, comment.taskId, comment.id);
      }
      return;
    }
    case 'taskLink': {
      const link = cache.links.get(id as TaskLinkId);
      if (link !== undefined) {
        idxPull(index.linksByTask, link.fromTaskId, link.id);
        idxPull(index.linksByTask, link.toTaskId, link.id);
      }
      return;
    }
    case 'timeEntry': {
      const entry = cache.timeEntries.get(id as TimeEntryId);
      if (entry !== undefined) {
        idxPull(index.timeByTask, entry.taskId, entry.id);
      }
      return;
    }
    case 'message': {
      const message = cache.messages.get(id as MessageId);
      if (message !== undefined) {
        idxPull(index.messagesByChannel, message.channelId, message.id);
        if (message.threadRootId !== null) {
          idxPull(index.repliesByThread, message.threadRootId, message.id);
        }
      }
      return;
    }
    case 'reaction': {
      const reaction = cache.reactions.get(id as ReactionId);
      if (reaction !== undefined) {
        idxPull(index.reactionsByOwner, `${reaction.ownerKind}:${String(reaction.ownerId)}`, reaction.id);
      }
      return;
    }
    case 'inboxItem': {
      const item = cache.inbox.get(id as InboxItemId);
      if (item !== undefined) {
        idxPull(index.inboxByUser, item.userId, item.id);
      }
      return;
    }
    default: {
      return;
    }
  }
}

function bySeq(cache: MockCache, ids: MessageId[]): MessageId[] {
  return ids
    .slice()
    .sort((a, b) => Number(cache.messages.get(a)?.seq ?? 0) - Number(cache.messages.get(b)?.seq ?? 0));
}

export function rebuildIndex(cache: MockCache): void {
  cache.index = freshIndex();
  for (const id of cache.tasks.keys()) {
    addToIndex(cache, 'task', id);
  }
  for (const id of cache.checklist.keys()) {
    addToIndex(cache, 'checklistItem', id);
  }
  for (const id of cache.comments.keys()) {
    addToIndex(cache, 'comment', id);
  }
  for (const id of cache.links.keys()) {
    addToIndex(cache, 'taskLink', id);
  }
  for (const id of cache.timeEntries.keys()) {
    addToIndex(cache, 'timeEntry', id);
  }
  for (const id of cache.messages.keys()) {
    addToIndex(cache, 'message', id);
  }
  for (const id of cache.reactions.keys()) {
    addToIndex(cache, 'reaction', id);
  }
  for (const id of cache.inbox.keys()) {
    addToIndex(cache, 'inboxItem', id);
  }
  for (const [channelId, ids] of cache.index.messagesByChannel) {
    cache.index.messagesByChannel.set(channelId, bySeq(cache, ids));
  }
  for (const [rootId, ids] of cache.index.repliesByThread) {
    cache.index.repliesByThread.set(rootId, bySeq(cache, ids));
  }
  for (const event of cache.activity) {
    for (const taskId of activityTaskIds(event)) {
      idxPush(cache.index.activityByTask, taskId, event.id);
    }
  }
}

export function activityTaskIds(event: ActivityEvent): TaskId[] {
  if (Array.isArray(event.taskIds)) {
    return event.taskIds;
  }
  if (event.entityTable === 'task') {
    return [event.entityId as TaskId];
  }
  const data = (event.after ?? event.before ?? {}) as Record<string, unknown>;
  const taskId = data['taskId'];
  if (typeof taskId === 'string') {
    return [taskId as TaskId];
  }
  if (event.entityTable === 'taskLink') {
    const ids: TaskId[] = [];
    const from = data['fromTaskId'];
    const to = data['toTaskId'];
    if (typeof from === 'string') {
      ids.push(from as TaskId);
    }
    if (typeof to === 'string' && to !== from) {
      ids.push(to as TaskId);
    }
    return ids;
  }
  return [];
}

type IndexName = Exclude<keyof MockCache['index'], 'taskByKey'>;
type IndexBucket = readonly [IndexName, string];
type IndexedTable = OpEntityTable | 'activityEvent';
type BucketMap = Map<string, string[]>;

export interface IndexChange {
  table: string;
  before: object | undefined;
  after: object | undefined;
}

const INDEXED_TABLES: ReadonlySet<string> = new Set<IndexedTable>([
  'task',
  'checklistItem',
  'comment',
  'taskLink',
  'timeEntry',
  'message',
  'reaction',
  'inboxItem',
  'activityEvent',
]);

const SORTED_INDEXES: ReadonlySet<IndexName> = new Set<IndexName>(['messagesByChannel', 'repliesByThread']);

function bucketsOf(table: IndexedTable, row: object): IndexBucket[] {
  switch (table) {
    case 'task': {
      const task = row as Task;
      if (task.deletedAt !== null) {
        return [];
      }
      const buckets: IndexBucket[] = [
        ['tasksByProject', String(task.projectId)],
        ['tasksByStatus', String(task.status)],
      ];
      if (task.assigneeId !== null) {
        buckets.push(['tasksByAssignee', String(task.assigneeId)]);
      }
      if (task.parentId !== null) {
        buckets.push(['subtasksByParent', String(task.parentId)]);
      }
      return buckets;
    }
    case 'checklistItem': {
      const item = row as ChecklistItem;
      return item.deletedAt === null ? [['checklistByTask', String(item.taskId)]] : [];
    }
    case 'comment': {
      const comment = row as Comment;
      return comment.deletedAt === null ? [['commentsByTask', String(comment.taskId)]] : [];
    }
    case 'taskLink': {
      const link = row as TaskLink;
      const buckets: IndexBucket[] = [['linksByTask', String(link.fromTaskId)]];
      if (link.toTaskId !== link.fromTaskId) {
        buckets.push(['linksByTask', String(link.toTaskId)]);
      }
      return buckets;
    }
    case 'timeEntry': {
      return [['timeByTask', String((row as TimeEntry).taskId)]];
    }
    case 'message': {
      const message = row as Message;
      return message.threadRootId === null
        ? [['messagesByChannel', String(message.channelId)]]
        : [['repliesByThread', String(message.threadRootId)]];
    }
    case 'reaction': {
      const reaction = row as Reaction;
      return [['reactionsByOwner', `${reaction.ownerKind}:${String(reaction.ownerId)}`]];
    }
    case 'inboxItem': {
      return [['inboxByUser', String((row as InboxItem).userId)]];
    }
    case 'activityEvent': {
      const taskIds = new Set(activityTaskIds(row as ActivityEvent).map(String));
      return [...taskIds].map((taskId): IndexBucket => ['activityByTask', taskId]);
    }
    default: {
      return [];
    }
  }
}

function tagOf([name, key]: IndexBucket): string {
  return `${name}\u0000${key}`;
}

function untag(tag: string): IndexBucket {
  const at = tag.indexOf('\u0000');
  return [tag.slice(0, at) as IndexName, tag.slice(at + 1)];
}

function bucketMap(cache: MockCache, name: IndexName): BucketMap {
  return cache.index[name] as unknown as BucketMap;
}

function rowsOf(cache: MockCache, table: IndexedTable): Iterable<object> {
  switch (table) {
    case 'task':
      return cache.tasks.values();
    case 'checklistItem':
      return cache.checklist.values();
    case 'comment':
      return cache.comments.values();
    case 'taskLink':
      return cache.links.values();
    case 'timeEntry':
      return cache.timeEntries.values();
    case 'message':
      return cache.messages.values();
    case 'reaction':
      return cache.reactions.values();
    case 'inboxItem':
      return cache.inbox.values();
    default:
      return cache.activity;
  }
}

function seqOf(cache: MockCache, id: string): number {
  return Number(cache.messages.get(id as MessageId)?.seq ?? 0);
}

function pullFromBucket(map: BucketMap, key: string, id: string): void {
  const list = map.get(key);
  const at = list?.indexOf(id) ?? -1;
  if (list === undefined || at < 0) {
    return;
  }
  if (list.length === 1) {
    map.delete(key);
  } else {
    list.splice(at, 1);
  }
}

function insertBySeq(cache: MockCache, map: BucketMap, key: string, id: string, isNew: boolean): boolean {
  const list = map.get(key) ?? [];
  if (list.includes(id)) {
    return true;
  }
  const seq = seqOf(cache, id);
  let low = 0;
  let high = list.length;
  while (low < high) {
    const middle = (low + high) >>> 1;
    if (seqOf(cache, list[middle] as string) <= seq) {
      low = middle + 1;
    } else {
      high = middle;
    }
  }
  if (!isNew && low > 0 && seqOf(cache, list[low - 1] as string) === seq) {
    return false;
  }
  list.splice(low, 0, id);
  map.set(key, list);
  return true;
}

function rescanBuckets(cache: MockCache, table: IndexedTable, tags: ReadonlySet<string>): void {
  const wanted = [...tags].map(untag);
  for (const [name, key] of wanted) {
    bucketMap(cache, name).delete(key);
  }
  for (const row of rowsOf(cache, table)) {
    for (const bucket of bucketsOf(table, row)) {
      if (tags.has(tagOf(bucket))) {
        const map = bucketMap(cache, bucket[0]);
        const list = map.get(bucket[1]);
        const id = (row as { id: string }).id;
        if (list === undefined) {
          map.set(bucket[1], [id]);
        } else {
          list.push(id);
        }
      }
    }
  }
  for (const [name, key] of wanted) {
    const list = bucketMap(cache, name).get(key);
    if (SORTED_INDEXES.has(name) && list !== undefined) {
      list.sort((a, b) => seqOf(cache, a) - seqOf(cache, b));
    }
  }
}

function endsWithNewRows(cache: MockCache, changes: readonly IndexChange[]): boolean {
  const added = changes.filter((change) => change.before === undefined).map((change) => change.after);
  const tail = cache.activity.slice(cache.activity.length - added.length);
  return tail.length === added.length && tail.every((event, at) => event === added[at]);
}

interface RowPlan {
  id: string;
  isNew: boolean;
  entering: IndexBucket[];
}

function planRow(table: IndexedTable, change: IndexChange): { plan: RowPlan; leaving: IndexBucket[] } {
  const { before, after } = change;
  const oldBuckets = before === undefined ? [] : bucketsOf(table, before);
  const newBuckets = after === undefined ? [] : bucketsOf(table, after);
  const resort =
    table === 'message' &&
    before !== undefined &&
    after !== undefined &&
    (before as Message).seq !== (after as Message).seq;
  const oldTags = new Set(oldBuckets.map(tagOf));
  const newTags = new Set(newBuckets.map(tagOf));
  const id = ((before ?? after) as { id: string }).id;
  return {
    leaving: oldBuckets.filter((bucket) => resort || !newTags.has(tagOf(bucket))),
    plan: {
      id,
      isNew: before === undefined,
      entering: newBuckets.filter((bucket) => resort || !oldTags.has(tagOf(bucket))),
    },
  };
}

function patchTaskKeys(cache: MockCache, changes: readonly IndexChange[]): void {
  for (const { before } of changes) {
    const task = before as Task | undefined;
    if (task !== undefined && cache.index.taskByKey.get(task.key) === task.id) {
      cache.index.taskByKey.delete(task.key);
    }
  }
  for (const { after } of changes) {
    const task = after as Task | undefined;
    if (task !== undefined) {
      cache.index.taskByKey.set(task.key, task.id);
    }
  }
}

function patchTable(cache: MockCache, table: IndexedTable, changes: readonly IndexChange[]): void {
  const planned = changes.map((change) => planRow(table, change));
  for (const { plan, leaving } of planned) {
    for (const [name, key] of leaving) {
      pullFromBucket(bucketMap(cache, name), key, plan.id);
    }
  }
  if (table === 'task') {
    patchTaskKeys(cache, changes);
  }
  const appendExact = table !== 'activityEvent' || endsWithNewRows(cache, changes);
  const rescan = new Set<string>();
  for (const { plan } of planned) {
    for (const bucket of plan.entering) {
      const [name, key] = bucket;
      const map = bucketMap(cache, name);
      if (SORTED_INDEXES.has(name)) {
        if (!insertBySeq(cache, map, key, plan.id, plan.isNew)) {
          rescan.add(tagOf(bucket));
        }
      } else if (plan.isNew && appendExact) {
        idxPush(map, key, plan.id);
      } else {
        rescan.add(tagOf(bucket));
      }
    }
  }
  if (rescan.size > 0) {
    rescanBuckets(cache, table, rescan);
  }
}

export function patchIndexes(cache: MockCache, changes: readonly IndexChange[]): void {
  const byTable = new Map<IndexedTable, IndexChange[]>();
  for (const change of changes) {
    if (INDEXED_TABLES.has(change.table)) {
      const table = change.table as IndexedTable;
      byTable.set(table, [...(byTable.get(table) ?? []), change]);
    }
  }
  for (const [table, tableChanges] of byTable) {
    patchTable(cache, table, tableChanges);
  }
}
